// DatasetSelector.tsx
import React, { useEffect, useState } from 'react'
import { Table, Form, Button, Row, Col, Alert, Card, Badge } from 'react-bootstrap'
import { Plus } from 'lucide-react'
import '../../styles/AdminDashboard.css'
import { type DatasetStats } from '../../utils/datasetStats'

interface DatasetRowProps {
  datasetSource: string
  isSelected: boolean
  onClick: () => void
  stats?: DatasetStats
  singleSelect: boolean
}

const DatasetRow: React.FC<DatasetRowProps> = ({
  datasetSource,
  isSelected,
  onClick,
  stats,
  singleSelect,
}) => {
  const isTraining = datasetSource.endsWith(':t')
  const name = datasetSource.split(':')[0]

  return (
    <tr
      onClick={onClick}
      className={isSelected ? 'bg-dark-subtle bg-opacity-10' : undefined}
      style={{
        cursor: 'pointer',
        transition: 'background-color 0.15s ease-in-out',
      }}
    >
      <td className="text-center" style={{ width: '40px' }}>
        <Form.Check
          type={singleSelect ? 'radio' : 'checkbox'}
          checked={isSelected}
          onChange={onClick}
          onClick={(e) => e.stopPropagation()}
        />
      </td>
      <td className={`text-light ${isSelected ? 'fw-medium' : ''}`}>{name}</td>
      <td className="text-center">
        <Badge bg={isTraining ? 'primary' : 'success'} className="text-uppercase">
          {isTraining ? 'Training' : 'Validation'}
        </Badge>
      </td>
      <td className="text-center">{Object.keys(stats?.annotations_per_class || {}).length}</td>
      <td className="text-center">
        {(stats?.total_unannotated_images || 0) + (stats?.total_annotated_images || 0)}
      </td>
      <td className="text-center">{stats?.total_annotations || 0}</td>
    </tr>
  )
}

interface DatasetSelectorProps {
  singleSelect?: boolean
  canCreate: boolean
  showAcceptButton?: boolean
  handleAcceptSelections: (sources: string[]) => void
  initialSelections: string[]
  availableDatasets: string[]
  datasetStats?: Record<string, DatasetStats>
}

type DatasetPurpose = 'Training' | 'Validation'

const DatasetSelector: React.FC<DatasetSelectorProps> = ({
  singleSelect = false,
  canCreate = true,
  showAcceptButton = true,
  handleAcceptSelections,
  initialSelections,
  availableDatasets,
  datasetStats = {},
}) => {
  const [isCreating, setIsCreating] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [newDatasetName, setNewDatasetName] = useState('')
  const [purposeFilter, setPurposeFilter] = useState<DatasetPurpose>('Training')
  const [filteredDatasets, setFilteredDatasets] = useState<string[]>([])
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>(initialSelections)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const allDatasets = Array.from(
      new Set([...(availableDatasets || []), ...(selectedDatasets || [])])
    )
    const filteredForName = searchTerm
      ? [...allDatasets.filter((ds) => ds.toLowerCase().includes(searchTerm.toLowerCase()))]
      : [...allDatasets]

    setFilteredDatasets(filteredForName || [])
  }, [availableDatasets, purposeFilter, searchTerm, selectedDatasets])

  useEffect(() => {
    if (newDatasetName.trim()) {
      setError(null)
    } else {
      setError('Please enter a dataset name')
    }
  }, [newDatasetName])

  const handleCreateDataset = () => {
    if (!newDatasetName?.trim()) {
      setError('Please enter a dataset name')
      return
    }

    const datasetId = `${newDatasetName.trim()}:${
      purposeFilter === 'Training' ? 't' : 'v'
    }`.toLowerCase()
    if ((availableDatasets || []).includes(datasetId)) {
      setError('Dataset name must be unique')
      return
    }

    // Update local state and trigger selection
    const newSelection = singleSelect ? [datasetId] : [...selectedDatasets, datasetId]
    setSelectedDatasets(newSelection)
    setNewDatasetName('')
    setError(null)
    setIsCreating(false)

    // Add this line to trigger the accept function after creating the dataset
    if (handleAcceptSelections) {
      handleAcceptSelections(newSelection)
    }
  }

  const handleToggleSelection = (datasetSource: string) => {
    let newSources: string[]
    if (!selectedDatasets.includes(datasetSource)) {
      newSources = singleSelect ? [datasetSource] : [...selectedDatasets, datasetSource]
    } else {
      newSources = selectedDatasets.filter((s: string) => s !== datasetSource)
    }
    setSelectedDatasets(newSources)

    // Only call handleAcceptSelections if it exists and we're in singleSelect mode
    if (singleSelect && !selectedDatasets.includes(datasetSource) && handleAcceptSelections) {
      handleAcceptSelections(newSources)
    }
  }

  const handleClearSelection = () => {
    setSelectedDatasets([])
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault()
        handleCreateDataset()
      }}
    >
      <Card className="bg-dark border-0">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="mb-0 text-light">Choose or Create a Dataset</h5>
          </div>

          {isCreating ? (
            <Card className="bg-dark border-secondary mb-4">
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="text-light">Dataset Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter dataset name..."
                        value={newDatasetName}
                        onChange={(e) => setNewDatasetName(e.target.value)}
                        className="bg-dark text-light border-secondary"
                        autoFocus
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="text-light">Dataset Purpose</Form.Label>
                      <div className="d-flex gap-3">
                        <Form.Check
                          type="radio"
                          id="training"
                          label="Training"
                          checked={purposeFilter === 'Training'}
                          onChange={() => setPurposeFilter('Training')}
                          className="text-light"
                        />
                        <Form.Check
                          type="radio"
                          id="validation"
                          label="Validation"
                          checked={purposeFilter === 'Validation'}
                          onChange={() => setPurposeFilter('Validation')}
                          className="text-light"
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                {error && (
                  <Alert variant="danger" className="mt-3 mb-0">
                    {error}
                  </Alert>
                )}

                <div className="d-flex justify-content-end gap-2 mt-4">
                  <Button variant="outline-secondary" onClick={() => setIsCreating(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="info">
                    Create Dataset
                  </Button>
                </div>
              </Card.Body>
            </Card>
          ) : (
            <>
              <div className="d-flex gap-3 mb-4">
                <div className="flex-grow-1 position-relative">
                  <Form.Control
                    autoFocus
                    type="text"
                    placeholder="Search datasets..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="bg-dark text-light border-secondary ps-5"
                  />
                  <i
                    className="bi bi-search position-absolute text-light"
                    style={{
                      left: '1rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                    }}
                  />
                </div>
                <Form.Select
                  value={purposeFilter}
                  onChange={(e) => setPurposeFilter(e.target.value as DatasetPurpose)}
                  className="bg-dark text-light border-secondary"
                  style={{ width: 'auto' }}
                >
                  <option value="Training">Training</option>
                  <option value="Validation">Validation</option>
                </Form.Select>
              </div>

              <Row className="align-items-center justify-content-center">
                {(selectedDatasets || []).length > 0 && (
                  <>
                    <Col xs={11} className="text-center">
                      <Alert variant="success" className="d-flex align-items-center my-2">
                        <span className="me-2">Selected:</span>
                        <strong>{selectedDatasets.map((ds) => ds.split(':')[0]).join(', ')}</strong>
                        <span className="mx-2 opacity-75">
                          (
                          {selectedDatasets
                            .map((ds) => (ds.endsWith(':t') ? 'Training' : 'Validation'))
                            .join(', ')}
                          )
                        </span>
                      </Alert>
                    </Col>
                    <Col xs={1} className="text-center justify-content-center">
                      <Button
                        variant="link"
                        className="text-success my-2 ms-auto"
                        onClick={handleClearSelection}
                      >
                        Clear
                      </Button>
                    </Col>
                  </>
                )}
              </Row>

              {(filteredDatasets || []).length > 0 ? (
                <div style={{ maxHeight: '60vh', overflowY: 'auto' }} className="pe-2">
                  <Table hover variant="dark" className="mb-0">
                    <thead className="sticky-top bg-dark">
                      <tr>
                        <th className="text-center" style={{ width: '40px' }}></th>
                        <th>Name</th>
                        <th className="text-center">Type</th>
                        <th className="text-center">Classes</th>
                        <th className="text-center">Images</th>
                        <th className="text-center">Annotations</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(filteredDatasets || [])
                        .filter((ds) =>
                          purposeFilter === 'Training' ? ds.endsWith(':t') : ds.endsWith(':v')
                        )
                        .map((dataset) => (
                          <DatasetRow
                            key={dataset}
                            datasetSource={dataset}
                            isSelected={(selectedDatasets || []).includes(dataset)}
                            onClick={() => handleToggleSelection(dataset)}
                            stats={datasetStats[dataset]}
                            singleSelect={singleSelect}
                          />
                        ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <Alert variant="info" className="text-center">
                  No datasets found matching your search.
                  {canCreate && (
                    <div className="mt-2">
                      <Button variant="outline-info" size="sm" onClick={() => setIsCreating(true)}>
                        Create New Dataset
                      </Button>
                    </div>
                  )}
                </Alert>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Form>
  )
}

export default DatasetSelector
// Path: /home/jason/src/AgileView/autovision-ui/src/components/DatasetSelector.tsx
